import * as React from 'react'

import type { ErrorLike } from '@apollo/client'

import type { BaseCommunityFilters } from '../../../CommunityFilters/types'
import { useMentionsQuery } from '../../../hooks/mentions/useMentionsQuery'
import type { MentionData } from '../../context'

export type UseMentionsDataProps = {
  channels: Parameters<typeof useMentionsQuery>[0]['channels']
  filters?: BaseCommunityFilters
}

export type MentionsData = {
  query: {
    loading: boolean
    error?: ErrorLike | null
    fetchingMore: boolean
    lastElementRef: (node: HTMLElement | null) => void
  }
  mentions: {
    list: MentionData[]
    hasMentions: boolean
  }
}

/**
 * 1:1 mirror of `Comments/CommentsList/hooks/useCommentsListData.ts` —
 * thin wrapper around the GraphQL query hook that reshapes the result
 * into the `{ query, mentions }` shape `MentionsList` consumes. Mirrors
 * `useCommentsData`'s return shape so the two list components read the
 * same.
 */
export const useMentionsData = ({
  channels,
  filters,
}: UseMentionsDataProps): MentionsData => {
  const { mentions, loading, error, fetchingMore, lastElementRef } =
    useMentionsQuery({
      channels,
      filters,
    })

  const hasMentions = mentions.length > 0

  return React.useMemo(
    () => ({
      query: {
        loading,
        error,
        fetchingMore,
        lastElementRef,
      },
      mentions: {
        list: mentions,
        hasMentions,
      },
    }),
    [loading, error, fetchingMore, lastElementRef, mentions, hasMentions],
  )
}
